import { useMemo } from 'react';
import { take, orderBy } from 'lodash';
import { intl } from '@fullstory/intl';

const RECENT_ITEMS_LIMIT_DEFAULT = 10;

function getNameAndIdComparator<T>(getItemProps: (item: T) => { id: string; label: string }) {
  return (a: T, b: T) => {
    const aProps = getItemProps(a);
    const bProps = getItemProps(b);
    return (
      aProps.label.toLowerCase().localeCompare(bProps.label.toLowerCase()) ||
      aProps.id.localeCompare(bProps.id)
    );
  };
}

export function useStarredRecentResources<T>({
  getItemProps,
  items,
  starredItems: starredItemsProp,
  recentItems: recentItemsProp,
  recentItemsLimit = RECENT_ITEMS_LIMIT_DEFAULT,
  starredLabel = intl.formatMessage({ defaultMessage: 'Starred', id: 'V7cUPv' }),
  recentLabel = intl.formatMessage({ defaultMessage: 'Recent', id: 'iXpep1' }),
  otherLabel = intl.formatMessage({ defaultMessage: 'All', id: 'zQvVDJ' }),
  includeGroupField = true,
}: {
  items: T[];
  starredItems?: T[];
  recentItems?: T[];
  recentItemsLimit?: number;
  getItemProps: (item: T) => {
    id: string;
    label: string;
    lastViewed?: Date;
    starred: boolean;
  };
  starredLabel?: string;
  recentLabel?: string;
  otherLabel?: string;
  includeGroupField?: boolean;
}) {
  const nameAndIdCompare = useMemo(() => getNameAndIdComparator(getItemProps), [getItemProps]);

  const starredItems = useMemo(
    () =>
      starredItemsProp ?? items.filter(item => getItemProps(item).starred).sort(nameAndIdCompare),
    [items, starredItemsProp, nameAndIdCompare, getItemProps],
  );

  const recentItems = useMemo(() => {
    if (recentItemsProp) return recentItemsProp;
    const viewedItems = items.filter(item => {
      const { starred, lastViewed } = getItemProps(item);
      return !starred && lastViewed;
    });
    const itemsByLastViewed = orderBy(viewedItems, item => getItemProps(item).lastViewed, 'desc');
    const recentItems = recentItemsLimit
      ? take(itemsByLastViewed, recentItemsLimit)
      : itemsByLastViewed;
    return recentItems.sort(nameAndIdCompare);
  }, [items, recentItemsProp, recentItemsLimit, nameAndIdCompare, getItemProps]);

  const otherItems = useMemo(
    () =>
      items
        .filter(item => !starredItems.includes(item) && !recentItems.includes(item))
        .sort(nameAndIdCompare),
    [items, starredItems, recentItems, nameAndIdCompare],
  );

  const groupedStarredItems = useMemo(
    () => starredItems.map(item => ({ ...item, group: starredLabel })),
    [starredItems, starredLabel],
  );
  const groupedRecentItems = useMemo(
    () => recentItems.map(item => ({ ...item, group: recentLabel })),
    [recentItems, recentLabel],
  );
  const groupedOtherItems = useMemo(
    () => otherItems.map(item => ({ ...item, group: otherLabel })),
    [otherItems, otherLabel],
  );

  return {
    starred: includeGroupField ? groupedStarredItems : starredItems,
    recent: includeGroupField ? groupedRecentItems : recentItems,
    other: includeGroupField ? groupedOtherItems : otherItems,
  };
}
